import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  Grid,
  Button
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useDataContext } from '../contexts/DataContext';
import PatientInfo from '../components/PatientInfo';
import ConsultationsList from '../components/ConsultationsList';
import VitalSignsChart from '../components/VitalSignsChart';
import TreatmentsList from '../components/TreatmentsList';
import MedicalHistoryList from '../components/MedicalHistoryList';
import PatientForm from '../components/PatientForm';
import ExportButtons from '../components/ExportButtons';
import MainLayout from '../components/MainLayout';

const TabPanel = ({ children, value, index, ...other }) => (
  <div hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const { patients, consultations, vitals, treatments, medicalHistory } = useDataContext();

  useEffect(() => {
    fetchPatientData();
  }, [id, patients, consultations, vitals, treatments, medicalHistory]);

  // Effet pour se synchroniser avec les changements de données
  useEffect(() => {
    if (patient && id) {
      // Filtrer les données liées à ce patient
      const patientConsultations = consultations.filter(c => c.patientId === id);
      const patientVitals = vitals.filter(v => v.patientId === id);
      const patientTreatments = treatments.filter(t => t.patientId === id);
      const patientMedicalHistory = medicalHistory.filter(h => h.patientId === id);
      
      setPatient(prev => ({
        ...prev,
        consultations: patientConsultations,
        vitalSigns: patientVitals,
        treatments: patientTreatments,
        medicalHistory: patientMedicalHistory
      }));
    }
  }, [consultations, vitals, treatments, medicalHistory, id, patient]);

  const fetchPatientData = async () => {
    try {
      setLoading(true);
      
      // D'abord, chercher le patient dans les données locales
      const localPatient = patients.find(p => p.id === id);
      
      if (localPatient) {
        // Si c'est un patient local (avec ID temporaire), charger seulement les données locales
        if (id.startsWith('temp_')) {
          setPatient({
            ...localPatient,
            consultations: [],
            vitalSigns: [],
            treatments: [],
            medicalHistory: []
          });
        } else {
          // Si c'est un patient du serveur, charger toutes les données
          try {
            // Filtrer les données liées à ce patient
            const patientConsultations = consultations.filter(c => c.patientId === id);
            const patientVitals = vitals.filter(v => v.patientId === id);
            const patientTreatments = treatments.filter(t => t.patientId === id);
            const patientMedicalHistory = medicalHistory.filter(h => h.patientId === id);
            
            setPatient({
              ...localPatient,
              consultations: patientConsultations,
              vitalSigns: patientVitals,
              treatments: patientTreatments,
              medicalHistory: patientMedicalHistory
            });
          } catch (apiError) {
            console.error('Erreur API, utilisation des données locales:', apiError);
            setPatient({
              ...localPatient,
              consultations: [],
              vitalSigns: [],
              treatments: [],
              medicalHistory: []
            });
          }
        }
      } else {
        // Patient non trouvé localement, essayer de le charger depuis le serveur
        try {
          // Patient non trouvé, rediriger vers la liste
          console.error('Patient non trouvé:', id);
          navigate('/patients');
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
          // Rediriger vers la liste des patients si le patient n'existe pas
          navigate('/patients');
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSave = () => {
    fetchPatientData();
    setFormOpen(false);
  };


  const handleEditPatient = () => {
    setFormOpen(true);
  };

  const handleCreatePatient = () => {
    setFormOpen(true);
  };

  if (loading) return <Typography>Chargement...</Typography>;
  
  // Si c'est un nouveau patient (ID = "new"), afficher le formulaire directement
          if (id === 'new') {
      return (
        <MainLayout>
          <Box>
            <Typography variant="h4" gutterBottom>
              Nouveau Patient
            </Typography>
            <PatientForm
              open={true}
              onClose={() => navigate('/patients')}
              patient={null}
              onSave={() => navigate('/patients')}
            />
          </Box>
        </MainLayout>
      );
  }
  
  if (!patient) return <Typography>Patient non trouvé</Typography>;

  return (
    <MainLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">
            {patient.firstName} {patient.lastName}
          </Typography>
          <Box display="flex" gap={2}>
            <ExportButtons 
              patientId={id} 
              patientName={`${patient.firstName}_${patient.lastName}`}
            />
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={handleEditPatient}
            >
              Modifier
            </Button>
          </Box>
        </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <PatientInfo patient={patient} />
        </CardContent>
      </Card>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
        <Tab label="Consultations" />
        <Tab label="Constantes Vitales" />
        <Tab label="Traitements" />
        <Tab label="Antécédents" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <ConsultationsList 
          consultations={patient.consultations} 
          patientId={id}
          onUpdate={fetchPatientData}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <VitalSignsChart 
          vitalSigns={patient.vitalSigns} 
          patientId={id}
          onUpdate={fetchPatientData}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <TreatmentsList 
          patientId={id} 
          onUpdate={fetchPatientData}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <MedicalHistoryList 
          patientId={id} 
          onUpdate={fetchPatientData}
        />
      </TabPanel>
      
      <PatientForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        patient={patient}
        onSave={handleFormSave}
      />
    </Box>
  </MainLayout>
);
};

export default PatientDetail;